/**
 * Event Health copy review: 273 profile entries / 257 distinct rule IDs.
 * Source: EVENT_HEALTH_COPY_REVIEW (1).xlsx, "Copy review", rows 6–278.
 * Numeric catalog paths are examples, NEVER runtime row addresses.
 * Keep reviewStatus so unresolved terminology remains visible to maintainers.
 */
export interface HealthRuleDefinition {
  readonly profiles: readonly string[];
  readonly field: string;
  readonly path: string;
  readonly title: string;
  readonly help: string;
  readonly reviewStatus: string;
}

export const HEALTH_RULES = {
  "AT-AQ-FEEDING-001": {
    "profiles": [
      "FEEDING"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Applied feed is missing",
    "help": "Add at least one feed.",
    "reviewStatus": "Draft ready"
  },
  "AT-AQ-FEEDING-003": {
    "profiles": [
      "FEEDING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Farm location is missing",
    "help": "Provide the feeding business location.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-005": {
    "profiles": [
      "FEEDING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the feeding record.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-006": {
    "profiles": [
      "FEEDING"
    ],
    "field": "feedType",
    "path": "/ilmd/gdst:feedType",
    "title": "Feed type is invalid",
    "help": "Provide Natural, processed, live, GMO, or other.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-007": {
    "profiles": [
      "FEEDING"
    ],
    "field": "proteinSource",
    "path": "/ilmd/gdst:proteinSource",
    "title": "Protein source is missing",
    "help": "Provide the protein source of the applied feed.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-008": {
    "profiles": [
      "FEEDING"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Feed lot is missing",
    "help": "Provide the applied feed lot number.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-EPC-001": {
    "profiles": [
      "FEEDING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A feed product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-AQ-FEEDING-QTY-001": {
    "profiles": [
      "FEEDING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A feed lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-AQ-FEEDING-QTY-002": {
    "profiles": [
      "FEEDING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Applied feed quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "AT-AQ-FEEDING-QTY-003": {
    "profiles": [
      "FEEDING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "AT-EPCIS-BIZ-TRANSACTION-001": {
    "profiles": [
      "*"
    ],
    "field": "bizTransactionList",
    "path": "/bizTransactionList",
    "title": "A business transaction is missing or invalid",
    "help": "Select an allowed transaction type and enter its reference.",
    "reviewStatus": "Draft ready"
  },
  "AT-GDST-CERTIFICATE-COMPLETE-001": {
    "profiles": [
      "*"
    ],
    "field": "certification",
    "path": "/cbvmda:certificationList/certification",
    "title": "Certificate information is incomplete",
    "help": "Complete the missing information for this certificate.",
    "reviewStatus": "Review / keep"
  },
  "AT-GDST-CERTIFICATE-COMPLETE-002": {
    "profiles": [
      "*"
    ],
    "field": "certification",
    "path": "/ilmd/cbvmda:certificationList/certification",
    "title": "Certificate information is incomplete",
    "help": "Complete the missing information for this certificate.",
    "reviewStatus": "Review / keep"
  },
  "AT-GDST-CERTIFICATE-TYPE-001": {
    "profiles": [
      "*"
    ],
    "field": "certificationType",
    "path": "/cbvmda:certificationList/certification",
    "title": "Certificate type is missing or invalid",
    "help": "Provide an allowed 1.2 certificate type for every certificate.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-001": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Stored products are missing",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-002": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-004": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Storage location is missing",
    "help": "Provide the cold-storage business location.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-COLD-006": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the event information.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-COLD-007": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "temperature",
    "path": "/sensorElementList/0/sensorReport/0",
    "title": "Storage temperature is missing",
    "help": "Provide a Temperature sensor report with value and unit of measure.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-COLD-EPC-001": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A stored product identifier is missing",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-QTY-001": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A stored lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-QTY-002": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Stored quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-COLD-QTY-003": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-COLD-TEMP-COMPLETE-001": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "temperature",
    "path": "/sensorElementList",
    "title": "Temperature report is incomplete",
    "help": "Complete the missing value or unit of measure for this Temperature report.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-RECV-DISPOSITION-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Receiving Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-SHIP-DISPOSITION-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Shipping Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-TRANSFER-001": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Transferred products are missing",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-TRANSFER-002": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-TRANSFER-003": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Transfer location is missing",
    "help": "Provide the transfer business location.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-TRANSFER-004": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "sourceList",
    "path": "/sourceList",
    "title": "Transfer source is missing",
    "help": "Provide at least one valid transfer source.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-TRANSFER-005": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "destinationList",
    "path": "/destinationList",
    "title": "Transfer destination is missing",
    "help": "Provide at least one valid transfer destination.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-TRANSFER-007": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the event information.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-TRANSFER-EPC-001": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A transferred product identifier is missing",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-TRANSFER-QTY-001": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A transferred lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "AT-SC-TRANSFER-QTY-002": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Transferred quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "AT-SC-TRANSFER-QTY-003": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "AT-WC-LAND-DISPOSITION-001": {
    "profiles": [
      "LANDING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Landing Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "AT-WC-TRANS-DISPOSITION-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Transshipment Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Harvested products are missing",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-003": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Farm location is missing",
    "help": "Provide the farm business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-004": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-005": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-006": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is invalid",
    "help": "Provide None, Internal, or 3P Audit.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-007": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "aquacultureMethod",
    "path": "/ilmd/gdst:aquacultureMethod",
    "title": "Aquaculture method is missing",
    "help": "Provide the farming method.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-008": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "productionMethod",
    "path": "/ilmd/cbvmda:productionMethodForFishAndSeafoodCode",
    "title": "Production method must be aquaculture",
    "help": "Provide AQUACULTURE as the production method.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-009": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Harvest lot is missing",
    "help": "Provide the harvest lot number.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-EPC-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A harvested product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-OUT-EPC-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-OUT-QTY-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-OUT-QTY-002": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-OUT-QTY-003": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-QTY-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A harvested lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-QTY-002": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Harvested quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FH-QTY-003": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FH-READPOINT-001": {
    "profiles": [
      "FARM_HARVEST"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Farm-harvest read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-TECH-EVENT-ID-001": {
    "profiles": [
      "FARM_HARVEST",
      "FEED_MILL",
      "FEEDING",
      "HATCHING",
      "AGGREGATION",
      "COLD_STORAGE",
      "COMMINGLING",
      "DISAGGREGATION",
      "PACKAGING",
      "PROCESSING",
      "RECEIVING",
      "SHIPPING",
      "TRANSFER",
      "FISHING",
      "LANDING",
      "PROCESSING_ON_VESSEL",
      "TRANSSHIPMENT"
    ],
    "field": "eventID",
    "path": "/eventID",
    "title": "Event identifier is missing or invalid",
    "help": "This identifier is generated by the application. Ask support to check the event record.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-001": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Feed products are missing",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-002": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Feed mill read point is missing",
    "help": "Provide the feed mill read point.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-003": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Feed mill business location is missing",
    "help": "Provide the feed mill business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-004": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-005": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-006": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is invalid",
    "help": "Provide None, Internal, or 3P Audit.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-007": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "proteinSource",
    "path": "/ilmd/gdst:proteinSource",
    "title": "Protein source is missing",
    "help": "Provide the source of protein used in the feed.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-008": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Feed lot is missing",
    "help": "Provide the feed lot number.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-EPC-001": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A feed product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-OUT-EPC-001": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-OUT-QTY-001": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-OUT-QTY-002": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-OUT-QTY-003": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-QTY-001": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A feed lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEED-QTY-002": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Feed quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEED-QTY-003": {
    "profiles": [
      "FEED_MILL"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-FEEDING-004": {
    "profiles": [
      "FEEDING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Feed owner is missing",
    "help": "Provide the owner of the applied feed.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-FEEDING-READPOINT-001": {
    "profiles": [
      "FEEDING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Feeding read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-001": {
    "profiles": [
      "HATCHING"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "Hatched products are missing",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-002": {
    "profiles": [
      "HATCHING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Hatchery location is missing",
    "help": "Provide the hatchery read point.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-003": {
    "profiles": [
      "HATCHING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Hatchery business location is missing",
    "help": "Provide the hatchery business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-004": {
    "profiles": [
      "HATCHING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-005": {
    "profiles": [
      "HATCHING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-006": {
    "profiles": [
      "HATCHING"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is invalid",
    "help": "Provide None, Internal, or 3P Audit.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-007": {
    "profiles": [
      "HATCHING"
    ],
    "field": "broodstockSource",
    "path": "/ilmd/gdst:broodstockSource",
    "title": "Broodstock source is invalid",
    "help": "Provide Domestic or Wild Sources.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-008": {
    "profiles": [
      "HATCHING"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Hatching lot is missing",
    "help": "Provide the hatching lot number.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-EPC-001": {
    "profiles": [
      "HATCHING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A hatched product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-OUT-EPC-001": {
    "profiles": [
      "HATCHING"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-OUT-QTY-001": {
    "profiles": [
      "HATCHING"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-OUT-QTY-002": {
    "profiles": [
      "HATCHING"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-OUT-QTY-003": {
    "profiles": [
      "HATCHING"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-QTY-001": {
    "profiles": [
      "HATCHING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A hatched lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-AQ-HATCH-QTY-002": {
    "profiles": [
      "HATCHING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Hatched quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-AQ-HATCH-QTY-003": {
    "profiles": [
      "HATCHING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-AGG-001": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "children",
    "path": "/childQuantityList",
    "title": "Aggregation children are missing",
    "help": "Provide childEPCs or childQuantityList.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-AGG-002": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "parentID",
    "path": "/parentID",
    "title": "Container identifier is missing or invalid",
    "help": "Select or enter a valid container identifier.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-AGG-003": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Aggregation Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-AGG-004": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Aggregation location is missing",
    "help": "Provide the aggregation location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-AGG-005": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Aggregation business location is missing",
    "help": "Provide the aggregation business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-AGG-006": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-AGG-007": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-AGG-EPC-001": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "childEPCs",
    "path": "/childEPCs",
    "title": "Product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-AGG-QTY-001": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "epcClass",
    "path": "/childQuantityList/0/epcClass",
    "title": "Product or lot is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-AGG-QTY-002": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "quantity",
    "path": "/childQuantityList/0/quantity",
    "title": "Quantity for this item is missing or invalid",
    "help": "Provide a quantity greater than zero for every item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-AGG-QTY-003": {
    "profiles": [
      "AGGREGATION"
    ],
    "field": "uom",
    "path": "/childQuantityList/0/uom",
    "title": "Unit of measure for this item is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-COLD-005": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the stored products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COLD-READPOINT-001": {
    "profiles": [
      "COLD_STORAGE"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Cold-storage read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-002": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Commingling location is missing",
    "help": "Provide the commingling business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-003": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-004": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-005": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Commingled lot is missing",
    "help": "Provide the new linking lot number.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-IN-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "inputs",
    "path": "/inputQuantityList",
    "title": "Commingling inputs are missing",
    "help": "Add at least one input.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-IN-EPC-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "inputEPCList",
    "path": "/inputEPCList",
    "title": "An input product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-IN-QTY-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "epcClass",
    "path": "/inputQuantityList/0/epcClass",
    "title": "An input lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-IN-QTY-002": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "quantity",
    "path": "/inputQuantityList/0/quantity",
    "title": "Input quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-IN-QTY-003": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "uom",
    "path": "/inputQuantityList/0/uom",
    "title": "Input unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-OUT-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "outputs",
    "path": "/outputQuantityList",
    "title": "Commingling outputs are missing",
    "help": "Add at least one output.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-OUT-EPC-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-OUT-QTY-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-OUT-QTY-002": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-COM-OUT-QTY-003": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-COM-READPOINT-001": {
    "profiles": [
      "COMMINGLING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Commingling read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-DISAGG-001": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "parentID",
    "path": "/parentID",
    "title": "Container identifier is missing or invalid",
    "help": "Select or enter a valid container identifier.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-DISAGG-002": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Disaggregation Workflow status must be inactive",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-DISAGG-003": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Disaggregation location is missing",
    "help": "Provide the disaggregation location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-DISAGG-004": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Disaggregation business location is missing",
    "help": "Provide the disaggregation business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-DISAGG-005": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-DISAGG-006": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-DISAGG-EPC-001": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "childEPCs",
    "path": "/childEPCs",
    "title": "Product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-DISAGG-QTY-001": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "epcClass",
    "path": "/childQuantityList/0/epcClass",
    "title": "Product or lot is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-DISAGG-QTY-002": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "quantity",
    "path": "/childQuantityList/0/quantity",
    "title": "Quantity for this item is missing or invalid",
    "help": "Provide a quantity greater than zero for every removed item.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-DISAGG-QTY-003": {
    "profiles": [
      "DISAGGREGATION"
    ],
    "field": "uom",
    "path": "/childQuantityList/0/uom",
    "title": "Unit of measure for this item is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Decision required"
  },
  "GDST12-SC-PACK-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-002": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Packaging location is missing",
    "help": "Provide the packaging business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PACK-003": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PACK-004": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PACK-IN-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "inputs",
    "path": "/inputQuantityList",
    "title": "Packaging inputs are missing",
    "help": "Add at least one input.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-IN-EPC-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "inputEPCList",
    "path": "/inputEPCList",
    "title": "An input product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-IN-QTY-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "epcClass",
    "path": "/inputQuantityList/0/epcClass",
    "title": "An input lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-IN-QTY-002": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "quantity",
    "path": "/inputQuantityList/0/quantity",
    "title": "Input quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PACK-IN-QTY-003": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "uom",
    "path": "/inputQuantityList/0/uom",
    "title": "Input unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-OUT-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "outputs",
    "path": "/outputQuantityList",
    "title": "Packaging outputs are missing",
    "help": "Add at least one output.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-OUT-EPC-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-OUT-QTY-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-OUT-QTY-002": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PACK-OUT-QTY-003": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PACK-READPOINT-001": {
    "profiles": [
      "PACKAGING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Packaging read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Processing Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-002": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Processing location is missing",
    "help": "Provide the business location where processing occurred.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-003": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the processed products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-004": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the event information.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-005": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Processing lot number is missing",
    "help": "Provide the linking lot number created by processing.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-007": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "countryOfOrigin",
    "path": "/ilmd/cbvmda:countryOfOrigin",
    "title": "Product origin is missing",
    "help": "Provide the country of origin for the processed product.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-008": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is missing or invalid",
    "help": "Provide None, Internal, or 3P Audit as the human welfare policy.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-IN-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "inputs",
    "path": "/inputQuantityList",
    "title": "A processing event must identify its inputs",
    "help": "Add at least one input.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-IN-EPC-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "inputEPCList",
    "path": "/inputEPCList",
    "title": "An input product identifier is missing",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-IN-QTY-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "epcClass",
    "path": "/inputQuantityList/0/epcClass",
    "title": "An input lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-IN-QTY-002": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "quantity",
    "path": "/inputQuantityList/0/quantity",
    "title": "Input quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every input quantity item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-IN-QTY-003": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "uom",
    "path": "/inputQuantityList/0/uom",
    "title": "Input unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-OUT-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "outputs",
    "path": "/outputQuantityList",
    "title": "A processing event must identify its outputs",
    "help": "Add at least one output.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-OUT-EPC-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-OUT-QTY-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-OUT-QTY-002": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every output quantity item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-PROC-OUT-QTY-003": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-PROC-READPOINT-001": {
    "profiles": [
      "PROCESSING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Processing read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-DESTINATION-OWNER-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "destinationList",
    "path": "/destinationList",
    "title": "Receiving destination owner is missing",
    "help": "Select the correct owner, or correct the owner in the linked location record.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-SOURCE-OWNER-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "sourceList",
    "path": "/sourceList",
    "title": "Receiving source owner is missing",
    "help": "Select the correct owner, or correct the owner in the linked location record.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "A receiving event must identify received products",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-002": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Receiving location is missing",
    "help": "Provide the business location where the products were received.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-003": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "sourceList",
    "path": "/sourceList",
    "title": "Receiving source is missing",
    "help": "Provide at least one valid source for the receiving event.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-004": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "destinationList",
    "path": "/destinationList",
    "title": "Receiving destination is missing",
    "help": "Provide at least one valid destination for the receiving event.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-005": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the received products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-006": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the event information.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-007": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is missing or invalid",
    "help": "Provide None, Internal, or 3P Audit as the human welfare policy.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-DISPOSITION-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Receiving Workflow status must be in transit",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-OUT-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A received product identifier is missing",
    "help": "Select or enter a product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-OUT-002": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A received lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-OUT-003": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Received quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every quantity-list item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-RECV-OUT-004": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-RECV-READPOINT-001": {
    "profiles": [
      "RECEIVING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Receiving read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-DESTINATION-OWNER-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "destinationList",
    "path": "/destinationList",
    "title": "Shipping destination owner is missing",
    "help": "Select the correct owner, or correct the owner in the linked location record.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-SOURCE-OWNER-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "sourceList",
    "path": "/sourceList",
    "title": "Shipping source owner is missing",
    "help": "Select the correct owner, or correct the owner in the linked location record.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "A shipping event must identify shipped products",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-002": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Shipping location is missing",
    "help": "Provide the business location from which the products were shipped.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-003": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "sourceList",
    "path": "/sourceList",
    "title": "Shipping source is missing",
    "help": "Provide at least one valid source for the shipping event.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-004": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "destinationList",
    "path": "/destinationList",
    "title": "Shipping destination is missing",
    "help": "Provide at least one valid destination for the shipping event.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-005": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the shipped products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-006": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the event information.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-007": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is missing or invalid",
    "help": "Provide None, Internal, or 3P Audit as the human welfare policy.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-DISPOSITION-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Shipping Workflow status must be in progress",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-OUT-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A shipped product identifier is missing",
    "help": "Select or enter a product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-OUT-002": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A shipped lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-OUT-003": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Shipped quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every quantity-list item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-SHIP-OUT-004": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-SC-SHIP-READPOINT-001": {
    "profiles": [
      "SHIPPING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Shipping read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-TRANSFER-006": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the transferred products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-SC-TRANSFER-READPOINT-001": {
    "profiles": [
      "TRANSFER"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Transfer read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-001": {
    "profiles": [
      "FISHING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Fishing Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-FISH-002": {
    "profiles": [
      "FISHING"
    ],
    "field": "lots",
    "path": "/quantityList",
    "title": "A fishing event must identify its output catch",
    "help": "Provide catch lots.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-003": {
    "profiles": [
      "FISHING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Fishing event location is missing",
    "help": "Provide event location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-004": {
    "profiles": [
      "FISHING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-005": {
    "profiles": [
      "FISHING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-006": {
    "profiles": [
      "FISHING"
    ],
    "field": "catchArea",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:catchArea",
    "title": "Catch area is missing",
    "help": "Provide catch area.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-007": {
    "profiles": [
      "FISHING"
    ],
    "field": "vesselTripDate",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/gdst:vesselTripDate",
    "title": "Vessel trip date is missing",
    "help": "Provide vessel trip date.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-009": {
    "profiles": [
      "FISHING"
    ],
    "field": "productionMethod",
    "path": "/ilmd/cbvmda:productionMethodForFishAndSeafoodCode",
    "title": "Production method is missing or invalid",
    "help": "Provide MARINE_FISHERY or INLAND_FISHERY for a wild-capture fishing event.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-014": {
    "profiles": [
      "FISHING"
    ],
    "field": "fishingGearDescription",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:fishingGearTypeCode",
    "title": "Fishing gear classification is missing or invalid",
    "help": "Select a fishing gear option with a supported classification code.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-FISH-CERT-001": {
    "profiles": [
      "FISHING"
    ],
    "field": "fishingAuthorization",
    "path": "/cbvmda:certificationList/certification/0",
    "title": "Fishing authorization is missing or incomplete",
    "help": "Provide a fishing authorization certification with its issuing agency and identifier.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-MD-001": {
    "profiles": [
      "FISHING"
    ],
    "field": "vesselName",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselName",
    "title": "Vessel name is missing",
    "help": "Provide vessel name.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-MD-002": {
    "profiles": [
      "FISHING"
    ],
    "field": "vesselID",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselID",
    "title": "Vessel registration is missing",
    "help": "Provide vessel registration.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-MD-003": {
    "profiles": [
      "FISHING"
    ],
    "field": "vesselFlagState",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselFlagState",
    "title": "Vessel flag is missing",
    "help": "Provide vessel flag.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-OUT-001": {
    "profiles": [
      "FISHING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A catch product identifier is missing",
    "help": "Select or enter a product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-FISH-OUT-002": {
    "profiles": [
      "FISHING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A catch lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-FISH-OUT-003": {
    "profiles": [
      "FISHING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Catch quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every quantity-list item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-FISH-OUT-004": {
    "profiles": [
      "FISHING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-LAND-001": {
    "profiles": [
      "LANDING"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "A landing event must identify landed products",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-LAND-003": {
    "profiles": [
      "LANDING"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Landing location is missing",
    "help": "Provide the location where landing occurred.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-004": {
    "profiles": [
      "LANDING"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Landing business location is missing",
    "help": "Provide the business location responsible for landing.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-005": {
    "profiles": [
      "LANDING"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the landed products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-006": {
    "profiles": [
      "LANDING"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the landing information.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-007": {
    "profiles": [
      "LANDING"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is missing or invalid",
    "help": "Provide None, Internal, or 3P Audit as the human welfare policy.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-CERT-001": {
    "profiles": [
      "LANDING"
    ],
    "field": "landingAuthorization",
    "path": "/cbvmda:certificationList/certification/0",
    "title": "Landing authorization is missing or incomplete",
    "help": "Provide a landing authorization with its issuing agency and identifier.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-DISPOSITION-001": {
    "profiles": [
      "LANDING"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Landing Workflow status must be in progress",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-LAND-OUT-001": {
    "profiles": [
      "LANDING"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A landed product identifier is missing",
    "help": "Select or enter a product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-LAND-OUT-002": {
    "profiles": [
      "LANDING"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A landed lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-LAND-OUT-003": {
    "profiles": [
      "LANDING"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Landed quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every landed quantity item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-LAND-OUT-004": {
    "profiles": [
      "LANDING"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Workflow status must be active",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-002": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Processing location is missing",
    "help": "Provide the vessel processing business location.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-003": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the product owner.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-004": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the information provider.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-005": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "lotNumber",
    "path": "/ilmd/cbvmda:lotNumber",
    "title": "Output lot is missing",
    "help": "Provide the linking lot number.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-006": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "vesselName",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselName",
    "title": "Vessel name is missing",
    "help": "Provide the vessel name.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-007": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "vesselID",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselID",
    "title": "Vessel registration is missing",
    "help": "Provide the vessel registration identifier.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-008": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "vesselFlagState",
    "path": "/ilmd/cbvmda:vesselCatchInformationList/cbvmda:vesselCatchInformation/0/cbvmda:vesselFlagState",
    "title": "Vessel flag state is missing",
    "help": "Provide the vessel flag state.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-IN-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "inputs",
    "path": "/inputQuantityList",
    "title": "Processing inputs are missing",
    "help": "Add at least one input.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-IN-EPC-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "inputEPCList",
    "path": "/inputEPCList",
    "title": "An input product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-IN-QTY-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "epcClass",
    "path": "/inputQuantityList/0/epcClass",
    "title": "An input lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-IN-QTY-002": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "quantity",
    "path": "/inputQuantityList/0/quantity",
    "title": "Input quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-IN-QTY-003": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "uom",
    "path": "/inputQuantityList/0/uom",
    "title": "Input unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-OUT-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "outputs",
    "path": "/outputQuantityList",
    "title": "Processing outputs are missing",
    "help": "Add at least one output.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-OUT-EPC-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "outputEPCList",
    "path": "/outputEPCList",
    "title": "An output product identifier is missing or invalid",
    "help": "Select or enter a valid product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-OUT-QTY-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "epcClass",
    "path": "/outputQuantityList/0/epcClass",
    "title": "An output lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-OUT-QTY-002": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "quantity",
    "path": "/outputQuantityList/0/quantity",
    "title": "Output quantity is invalid",
    "help": "Provide a quantity greater than zero.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-POV-OUT-QTY-003": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "uom",
    "path": "/outputQuantityList/0/uom",
    "title": "Output unit is invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-POV-READPOINT-001": {
    "profiles": [
      "PROCESSING_ON_VESSEL"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "On-vessel processing read point is missing or invalid",
    "help": "Provide the geographic or business location where the event took place.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "products",
    "path": "/quantityList",
    "title": "A transshipment event must identify products",
    "help": "Add at least one product.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-TRANS-002": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "readPoint",
    "path": "/readPoint/id",
    "title": "Transshipment location is missing",
    "help": "Provide the location where transshipment occurred.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-003": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "bizLocation",
    "path": "/bizLocation/id",
    "title": "Transshipment business location is missing",
    "help": "Provide the business location responsible for transshipment.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-004": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "productOwner",
    "path": "/gdst:productOwner",
    "title": "Product owner is missing",
    "help": "Provide the owner of the transshipped products.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-005": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "informationProvider",
    "path": "/cbvmda:informationProvider",
    "title": "Information provider is missing",
    "help": "Provide the party responsible for the transshipment information.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-006": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "humanWelfarePolicy",
    "path": "/gdst:humanWelfarePolicy",
    "title": "Human welfare policy is missing or invalid",
    "help": "Provide None, Internal, or 3P Audit as the human welfare policy.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-CERT-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "transshipmentAuthorization",
    "path": "/cbvmda:certificationList/certification/0",
    "title": "Transshipment authorization is missing or incomplete",
    "help": "Provide a transshipment authorization with its issuing agency and identifier.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-DISPOSITION-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "disposition",
    "path": "/disposition",
    "title": "Transshipment Workflow status must be in progress",
    "help": "This value is set by the event workflow. Ask support to check how the event was generated.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-TRANS-OUT-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "epcList",
    "path": "/epcList",
    "title": "A transshipped product identifier is missing",
    "help": "Select or enter a product identifier for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-TRANS-OUT-002": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "epcClass",
    "path": "/quantityList/0/epcClass",
    "title": "A transshipped lot identifier is missing",
    "help": "Select the product and lot for each item.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-TRANS-OUT-003": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "quantity",
    "path": "/quantityList/0/quantity",
    "title": "Transshipped quantity is missing or invalid",
    "help": "Provide a quantity greater than zero for every transshipped quantity item.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-OUT-004": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "uom",
    "path": "/quantityList/0/uom",
    "title": "Unit of measure is missing or invalid",
    "help": "Select an accepted unit of measure.",
    "reviewStatus": "Draft ready"
  },
  "GDST12-WC-TRANS-VESSEL-001": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "vesselName",
    "path": "/cbvmda:vesselName",
    "title": "Transshipment vessel name is missing",
    "help": "Provide the name of the receiving transshipment vessel.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-VESSEL-002": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "vesselID",
    "path": "/cbvmda:vesselID",
    "title": "Transshipment vessel registration is missing",
    "help": "Provide the registration of the receiving transshipment vessel.",
    "reviewStatus": "Review / keep"
  },
  "GDST12-WC-TRANS-VESSEL-003": {
    "profiles": [
      "TRANSSHIPMENT"
    ],
    "field": "vesselFlagState",
    "path": "/cbvmda:vesselFlagState",
    "title": "Transshipment vessel flag is missing",
    "help": "Provide the flag state of the receiving transshipment vessel.",
    "reviewStatus": "Review / keep"
  }
} as const satisfies Record<string, HealthRuleDefinition>;

export type HealthRuleId = keyof typeof HEALTH_RULES;
export const HEALTH_RULE_IDS = Object.keys(HEALTH_RULES) as HealthRuleId[];

/** Derived once: never maintain an independent field-to-rule list. */
export const HEALTH_RULES_BY_FIELD: Readonly<Record<string, readonly HealthRuleId[]>> =
  HEALTH_RULE_IDS.reduce<Record<string, HealthRuleId[]>>((index, code) => {
    (index[HEALTH_RULES[code].field] ??= []).push(code);
    return index;
  }, {});

